import { GraphQLRequestError } from '../graphql/routing/apiRouting';
import { isRecord } from './onboardingValidation';

export type OnboardingInputField = 'name' | 'phone' | 'email' | 'password';

export type OnboardingError =
  | { kind: 'rescan-required' }
  | { kind: 'invalid-input'; field: OnboardingInputField }
  | { kind: 'email-exists' }
  | { kind: 'invalid-request' }
  | { kind: 'retryable' };

type OnboardingErrorExtension = {
  error_code?: unknown;
  rescan_required?: unknown;
  field?: unknown;
};

const INPUT_FIELDS: readonly string[] = ['name', 'phone', 'email', 'password'];

const retryableError = (): OnboardingError => ({ kind: 'retryable' });

const isOnboardingInputField = (value: unknown): value is OnboardingInputField =>
  typeof value === 'string' && INPUT_FIELDS.includes(value);

const getExtensions = (error: unknown): OnboardingErrorExtension | null => {
  if (!isRecord(error) || !isRecord(error.extensions)) return null;

  return error.extensions;
};

const classifyNonRescanError = (extensions: OnboardingErrorExtension | null): OnboardingError | null => {
  if (!extensions || extensions.rescan_required !== false) return null;

  if (
    extensions.error_code === 'CANVASSER_ONBOARDING_INVALID_INPUT' &&
    isOnboardingInputField(extensions.field)
  ) {
    return { kind: 'invalid-input', field: extensions.field };
  }

  if (
    extensions.error_code === 'CANVASSER_ONBOARDING_EMAIL_EXISTS' &&
    extensions.field === 'email'
  ) {
    return { kind: 'email-exists' };
  }

  return null;
};

const areSameOnboardingError = (first: OnboardingError, second: OnboardingError): boolean =>
  first.kind === second.kind &&
  (first.kind !== 'invalid-input' || second.kind !== 'invalid-input' || first.field === second.field);

// Only the documented extension fields control onboarding behavior. Human-readable
// GraphQL messages and generic `extensions.code` values are deliberately ignored.
export const classifyOnboardingGraphQLErrors = (errors: readonly unknown[]): OnboardingError => {
  const extensions = errors.map(getExtensions);

  // Rescanning is safer than correction whenever any returned error requires it,
  // including mixed/malformed error lists and duplicate-email races.
  if (extensions.some(extension => extension?.rescan_required === true)) {
    return { kind: 'rescan-required' };
  }

  const classifiedErrors = extensions.map(classifyNonRescanError);

  if (classifiedErrors.length === 0 || classifiedErrors.some(error => error === null)) {
    return retryableError();
  }

  const firstError = classifiedErrors[0];
  if (!firstError || !classifiedErrors.every(error => error !== null && areSameOnboardingError(firstError, error))) {
    return retryableError();
  }

  return firstError;
};

// Converts request failures to a secret-free, UI-ready semantic state. Non-GraphQL
// failures and malformed responses deliberately fail closed as generic retryable.
export const classifyOnboardingError = (error: unknown): OnboardingError => {
  if (!(error instanceof GraphQLRequestError)) return retryableError();

  return classifyOnboardingGraphQLErrors(error.graphQLErrors ?? []);
};
